/** @jsxImportSource @emotion/react */
import * as tagSelectors from "./../tag/tagSelectors";
import * as React from "react";
import { useSelector } from "react-redux";
import * as Inputs from "../common/hook-form/index";
import { Venue } from "../../model/Venue";
import * as venueSelectors from "./venueSelectors";
import * as organizationSelectors from "../organization/organizationSelectors";
import { VenueTag } from "../../model/VenueTag";
import { RootState } from "../../store";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { TestIdsEnum } from "../../TestIdsEnum";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useUpdateFormErrors from "../common/hook-form/useUpdateFormErrors";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import _ from "lodash";
import VenueUpsertFormImage from "./VenueUpsertFormImage";
import * as authSelectors from "../auth/authSelectors";
import clsx from "clsx";
import useExposeFormApi from "../common/hook-form/useExposeFormApi";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { FormRow } from "../common/hook-form/index";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { TextAreaInput } from "../../components/forms/inputs/TextAreaInput";
import { css } from "@emotion/react";
import { CheckboxInput } from "../../components/forms/inputs/CheckboxInput";
import { useGetCountries } from "../../service/api";
import { SpinnerBlock } from "../common/Spinner";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { useHandleFetchError } from "../../error-handler";
import { ButtonAction, ButtonPrimary } from "../../components/NewButtons";
import { breakpoints } from "../../components/breakpoints";
import { DatePickerInput } from "../../components/forms/inputs/DatePickerInput";
import { TimePickerInput } from "../../components/forms/inputs/TimePickerInput";
import { IconButton } from "../../components/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SpecialOpeningsHoursPayload } from "../../model/OpeningHours";
import { CheckboxField } from "../../components/forms/fields/CheckboxField";
import { formatDateForAPI } from "../../util";
import {
  AddressParts,
  addressPartsFromFormValues,
  addressPartsFromResource,
} from "../../model/Address";
import {
  AddressPartsInputs,
  addressPartsSchema,
} from "../../components/forms/inputs/AddressPartsInputs";

interface VenueData
  extends Omit<
      Venue,
      "tags" | "specialOpeningHours" | "address" | "addressParts"
    >,
    AddressParts {
  tags: {
    [key: string]: boolean;
  };
  specialOpeningHours: SpecialOpeningsHoursPayload[];
  isEnabled?: boolean;
}

type VenueUpsertPayload = Omit<
  VenueData,
  "_type" | "id" | "shopId" | "shopName" | "tags"
> & {
  tags: Array<{ id: string }>;
};

const formRowStyle = css`
  margin-top: 16px;
`;

const checkboxWrapperStyle = css`
  display: flex;
  align-items: center;
  width: 100px;
  min-width: 100px;
`;

const advancedSectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const openingHoursWrapperStyle = css`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const checkboxGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const additionalCheckboxGroupStyle = css`
  margin-top: 16px;
`;

const openingHourWrapperStyle = css`
  ${breakpoints.mobile} {
    margin-bottom: 16px;
  }
`;

const specialOpeningHoursWrapperStyle = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

interface Props {
  onSubmit: (
    data: any,
    filesToUpload: File[],
    venueLogo: File | undefined
  ) => Promise<void>;
  onDeleteImage: (imageUrl: string) => Promise<void>;
  venue: Venue | Partial<Venue>;
  isLoading: boolean;
}

const venueToFormData = (
  tags: Readonly<VenueTag[]>,
  venue: Venue | Partial<Venue>
): VenueData | Partial<VenueData> => {
  venue = _.cloneDeep(venue);

  if (!venue.openingHours) {
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    venue.openingHours = days.map((day) => ({
      day,
      open: "",
      close: "",
      minimum_age: 0,
    }));
  }

  const { specialOpeningHours, address, addressParts, ...rest } = venue;

  const specialOpeningHours_ = (specialOpeningHours || [])?.map((o) => ({
    ...o,
    is_open: o.is_open,
    minimum_age: 0,
  }));

  let tagsHash: Record<string, boolean> = tags.reduce((p, tag) => {
    p[tag.id] = false;
    return p;
  }, {} as Record<string, boolean>);

  if (Array.isArray(venue.tags)) {
    const tagValues = venue.tags.reduce((acc, curr) => {
      acc[curr.id] = true;
      return acc;
    }, {} as Record<string, boolean>);

    tagsHash = {
      ...tagsHash,
      ...tagValues,
    };
  }

  let formData = {
    ...rest,
    ...addressPartsFromResource(addressParts),
    specialOpeningHours: specialOpeningHours_,
    tags: tagsHash,
  };

  if (venue.eventDate) {
    formData.eventDate = moment(venue.eventDate).format();
  }

  return formData;
};

const formDataToVenue = (values: VenueData): VenueUpsertPayload => {
  // Response-only fields the form was reset with; the upsert request does not take them.
  const { _type, id, shopId, shopName, tags: tagFlags, ...rest } = values;

  const tags = _.toPairs(tagFlags).reduce((acc, pair) => {
    if (pair[1]) {
      acc.push({ id: pair[0] });
    }

    return acc;
  }, [] as Array<{ id: string }>);

  let r: VenueUpsertPayload = {
    ...rest,
    ...addressPartsFromFormValues(values),
    tags,
  };

  if (values.eventDate) {
    r.eventDate = moment(values.eventDate).format();
  }

  return r;
};

export interface VenueUpsertFormRefHandle {
  isValid: () => Promise<boolean>;
  submit: () => Promise<any>;
}

const VenueUpsertForm = React.forwardRef<VenueUpsertFormRefHandle, Props>(
  (props, ref) => {
    const { t } = useTranslation();
    const venue = props.venue;
    const schema = yup.object().shape({
      title: yup.string().min(3, t("venue_upsert_form.errors.title.too_short")),
      age: yup.number().positive().integer(),
      externalLinkUrl: yup
        .string()
        .url(t("venue_upsert_form.errors.external_link_url.invalid")),
      countryCode: yup.string().required("Choose country to continue"),
      ...addressPartsSchema,
      longitude: yup.number(),
      latitude: yup.number(),
      specialOpeningHours: yup
        .array()
        .of(
          yup.object().shape({
            date: yup.string().required("Date is required"),
            is_open: yup.boolean().required("Is open is required"),
            open: yup.string().when("is_open", {
              is: true,
              then: yup.string().required("Open time is required"),
              otherwise: yup.string().optional(),
            }),
            close: yup.string().when("is_open", {
              is: true,
              then: yup.string().required("Close time is required"),
              otherwise: yup.string().optional(),
            }),
          })
        )
        .optional(),
      radius: yup
        .number()
        .typeError("Radius must be a number")
        .min(0, "Radius cannot be below 0")
        .max(100000000, "Radius cannot be higher than 100000000"),
    });

    const [filesToUpload, setFilesToUpload] = React.useState<File[]>([]);
    const [venueLogo, setVenueLogo] = React.useState<File | undefined>();
    const {
      data: countries,
      isLoading: countriesAreLoading,
      error: getCountriesError,
    } = useGetCountries();

    const formMethods = useForm<VenueData>({
      resolver: yupResolver(schema),
      reValidateMode: "onChange",
      defaultValues: {
        exposeLocation: true,
        specialOpeningHours: [],
      },
    });

    const {
      fields: specialOpeningHoursFields,
      append: appendSpecialOpeningHours,
      remove: removeSpecialOpeningHours,
    } = useFieldArray({
      control: formMethods.control,
      name: "specialOpeningHours",
    });

    const { handleSubmit, setError } = formMethods;

    const organization = useSelector(authSelectors.selectedOrganizationKeyName);
    const isShowPersonalCodeVisible = useSelector((state: RootState) =>
      organizationSelectors.isVenueUpsertFormFieldVisible(
        state,
        "showPersonalCode"
      )
    );
    const isShowMemberCardVisible = useSelector((state: RootState) =>
      organizationSelectors.isVenueUpsertFormFieldVisible(
        state,
        "showMemberCard"
      )
    );
    const isEventDateVisible = useSelector((state: RootState) =>
      organizationSelectors.isVenueUpsertFormFieldVisible(state, "eventDate")
    );
    const errors = useSelector(venueSelectors.editFormErrors);

    useUpdateFormErrors(errors, setError);

    const tags = useSelector(tagSelectors.tagsForCurrentOrganization);
    const tagFormValues = React.useMemo(() => {
      if (!Array.isArray(tags)) {
        return [];
      }

      return tags.map((x) => ({ id: x.id, label: x.title }));
    }, [tags]);

    const reset = formMethods.reset;
    React.useEffect(
      function loadFormDefaults() {
        if (venue) {
          let venueData = venueToFormData(tags || [], venue);
          venueData = {
            eventDate: venue.eventDate
              ? moment(venue.eventDate).format("YYYY-MM-DD")
              : undefined,
            isEnabled: !venue.isDisabled,
            radius: venue.radius ? venue.radius : 99999999,
            ...venueData,
          };
          reset(venueData);
        } else {
          let venueData = venueToFormData(tags || [], venue);
          reset(venueData);
        }
      },
      [reset, tags, venue]
    );

    const onSubmit = async (values: VenueData) => {
      const v = {
        ...values,
        openingHours: values.openingHours.map((o) => ({
          ...o,
          open: o.open ? moment(o.open, "HH:mm:ss").format("HH:mm:ss") : "",
          close: o.close ? moment(o.close, "HH:mm:ss").format("HH:mm:ss") : "",
        })),
        specialOpeningHours: values.specialOpeningHours.map((o) => ({
          is_open: o.is_open,
          date: formatDateForAPI(o.date),
          minimum_age: 0,
          open: o.open ? moment(o.open, "HH:mm:ss").format("HH:mm:ss") : "",
          close: o.close ? moment(o.close, "HH:mm:ss").format("HH:mm:ss") : "",
        })),
        isDisabled: !values.isEnabled,
      };
      delete v.isEnabled;

      await props.onSubmit(formDataToVenue(v), filesToUpload, venueLogo);
    };

    useExposeFormApi(ref, formMethods, onSubmit);

    const onFilesSelect = (files: File[]) => {
      setFilesToUpload(filesToUpload.concat(files));
    };

    const removeFileToUpload = (file: File) => {
      setFilesToUpload(
        filesToUpload.filter((fileToUpload) => fileToUpload !== file)
      );
    };

    useHandleFetchError(getCountriesError);

    if (countriesAreLoading) {
      return <SpinnerBlock />;
    }

    const countryOptions = (countries || [])
      .filter((c) => c.enabled)
      .sort()
      .map((o) => ({
        label: o.englishName,
        value: o.countryCode,
      }));

    return (
      <FormProvider {...formMethods}>
        <form
          className="card"
          // @ts-ignore
          onSubmit={handleSubmit(onSubmit)}
          data-test={TestIdsEnum.VenueUpsertForm}
        >
          <div className="card-header">
            <StyledText
              color={colors.textPrimary}
              className="bodyHeaderOneRegular card-title"
            >
              {venue.id ? "Edit restaurant" : "Create restaurant"}
            </StyledText>
          </div>
          <div className="card-body">
            <div>
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                Standard restaurant information
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-6"
              >
                Below is the standard required information to create a
                restaurant. You cannot create a restaurant if the fields are not
                filled.
              </StyledText>
            </div>
            <FormRow>
              <TextInput
                name="title"
                label={t("venue_upsert_form.title.label")}
                helperText="The official venue name that users will see in app and on webpage."
              />
              <SelectInput
                label="Country code"
                name="countryCode"
                options={countryOptions}
              />
            </FormRow>
            <AddressPartsInputs css={formRowStyle} />
            <div
              className={clsx({
                "form-group": true,
                "has-error": !!formMethods.formState.errors?.["images"],
              })}
            >
              <Inputs.DropzoneFormField
                name="images"
                helpText={t("venue_upsert_form.images.help_text")}
                label="Restaurant image"
                onDrop={onFilesSelect}
                selectedFiles={filesToUpload}
                onSelectedFileRemove={removeFileToUpload}
                accept=".jpg,.jpeg,.png"
              >
                <div className="row">
                  {venue.images?.map((image, i) => (
                    <React.Fragment key={image.url}>
                      <VenueUpsertFormImage
                        organization={organization}
                        venueId={venue.id}
                        image={image}
                        showDeleteButton={true}
                      />
                      {(i + 1) % 4 === 0 && <div className="w-100" />}
                    </React.Fragment>
                  ))}
                </div>
              </Inputs.DropzoneFormField>
              <StyledText
                color={colors.textSecondary}
                className="bodySubtextRegular mb-0"
              >
                This is the official restaurant image that users will see in app
                and on web.
              </StyledText>
            </div>
            <TextAreaInput
              name="description"
              label={t("venue_upsert_form.description.label")}
              rows={6}
              helperText="Use the description to inform users about restaurant information. This will be visible for users of the app and website."
            />
            <FormRow css={formRowStyle}>
              <TextInput
                name="latitude"
                label={t("venue_upsert_form.latitude.label")}
                helperText="Go to www.latlong.net to find the GPS coordinates of your location."
              />
              <TextInput
                label={t("venue_upsert_form.longitude.label")}
                name="longitude"
                helperText="Go to www.latlong.net to find the GPS coordinates of your location."
              />
            </FormRow>
            <div className="mt-4">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                Internal notes
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-6"
              >
                This will only be visible for admins. You can use this space for
                relevant information about the restaurant. E.g. Restaurant
                manager and contact information for the manager.
              </StyledText>
            </div>
            <TextAreaInput
              name="internalNote"
              label={t("venue_upsert_form.internal_note.label")}
              rows={3}
            />
            <div className="mt-6">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                Restaurant configuration
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-6"
              >
                Add tags to the restaurant, expose location and enable
                restaurant to users in app and on website
              </StyledText>
            </div>
            <FormRow style={{ gap: "32px" }}>
              <div css={checkboxGroupStyle}>
                <StyledText
                  color={colors.textPrimary}
                  className="bodySubtextSemibold mb-0"
                >
                  {`${t("venue_upsert_form.tags.label")}`}
                </StyledText>
                {tagFormValues.map((tag, i) => (
                  <CheckboxInput
                    key={i}
                    name={`tags.${tag.id}`}
                    value={tag.id}
                    label={tag.label}
                  />
                ))}
              </div>
              <div css={checkboxGroupStyle}>
                <StyledText
                  color={colors.textPrimary}
                  className="bodySubtextSemibold mb-0"
                >
                  Visibility in app
                </StyledText>
                <CheckboxInput
                  name="isHidden"
                  label="Hide restaurant in app and webshop. OBS: If this is checked, customers will NOT see this restaurant in the app and webshop."
                />
              </div>
            </FormRow>
            <div className="mt-4">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                Opening hours
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-6"
              >
                Configure the standard opening hours for this restaurant
              </StyledText>
            </div>
            {formMethods.getValues()?.openingHours?.map((oh: any, i: any) => (
              <div key={i} css={openingHoursWrapperStyle} className="row">
                <input
                  type="hidden"
                  {...formMethods.register(`openingHours.${i}.day` as any)}
                />
                <div className="col-md-2 text-left">
                  <StyledText className="bodyTextSemibold mt-3 text-capitalize">
                    {oh.day}
                  </StyledText>
                </div>
                <div className="col-md-10">
                  <div className="row">
                    <div css={openingHourWrapperStyle} className="col-md-4">
                      <TextInput
                        name={`openingHours.${i}.open`}
                        helperText="Opening hour"
                        type="time"
                      />
                    </div>
                    <div css={openingHourWrapperStyle} className="col-md-4">
                      <TextInput
                        name={`openingHours.${i}.close`}
                        helperText="Closing hour"
                        type="time"
                      />
                    </div>
                    <div css={openingHourWrapperStyle} className="col-md-4">
                      <TextInput
                        name={`openingHours.${i}.minimum_age`}
                        helperText="Minimum age"
                        type="number"
                        min={0}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="mt-4">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                Special opening hours
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-6"
              >
                Set special opening hours. They will override your normal
                opening hours.
              </StyledText>
            </div>
            <div css={specialOpeningHoursWrapperStyle}>
              {specialOpeningHoursFields.map((field: any, index: number) => (
                <FormRow key={field.id}>
                  <div css={checkboxWrapperStyle}>
                    <Controller
                      control={formMethods.control}
                      name={`specialOpeningHours.${index}.is_open`}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <CheckboxField
                          label="Closed"
                          checked={!value}
                          onBlur={onBlur}
                          onChange={(e) => {
                            if (e.target.checked) {
                              formMethods.setValue(
                                `specialOpeningHours.${index}.open`,
                                ""
                              );
                              formMethods.setValue(
                                `specialOpeningHours.${index}.close`,
                                ""
                              );
                            }
                            onChange(!e.target.checked);
                          }}
                          innerRef={ref}
                        />
                      )}
                    />
                  </div>
                  <DatePickerInput
                    name={`specialOpeningHours.${index}.date`}
                    label="Date"
                    minDate={new Date()}
                  />
                  <TimePickerInput
                    name={`specialOpeningHours.${index}.open`}
                    label="Opening hour"
                    disabled={
                      !formMethods.watch(`specialOpeningHours.${index}.is_open`)
                    }
                  />
                  <TimePickerInput
                    name={`specialOpeningHours.${index}.close`}
                    label="Closing hour"
                    disabled={
                      !formMethods.watch(`specialOpeningHours.${index}.is_open`)
                    }
                  />
                  <IconButton
                    icon={<FontAwesomeIcon icon={solid("trash")} />}
                    onClick={() => removeSpecialOpeningHours(index)}
                  />
                </FormRow>
              ))}
              <ButtonPrimary
                onClick={() =>
                  appendSpecialOpeningHours({
                    date: "",
                    is_open: true,
                    open: "",
                    close: "",
                  })
                }
              >
                Add special opening hour
              </ButtonPrimary>
            </div>
            <div className="mt-4">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi mb-2"
              >
                Advanced information
              </StyledText>
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-6"
              >
                Below is the advanced information. These should not be filled
                out when creating a restaurant.
              </StyledText>
            </div>
            <div className="row">
              <div css={advancedSectionStyle} className="col-12">
                <TextInput
                  name="benefitTagline"
                  label={t("venue_upsert_form.benefit_tagline.label")}
                  helperText={t("venue_upsert_form.benefit_tagline.help_text")}
                />
                <TextAreaInput
                  name="benefitDescription"
                  label={t("venue_upsert_form.benefit_description.label")}
                  helperText={t(
                    "venue_upsert_form.benefit_description.help_text"
                  )}
                  rows={3}
                />
                <TextInput
                  name="externalLinkTitle"
                  label={t("venue_upsert_form.external_link_title.label")}
                  helperText={t(
                    "venue_upsert_form.external_link_title.help_text"
                  )}
                />
                <TextInput
                  name="externalLinkUrl"
                  label={t("venue_upsert_form.external_link_url.label")}
                  helperText={t(
                    "venue_upsert_form.external_link_url.help_text"
                  )}
                />
                <TextInput
                  name="radius"
                  type="number"
                  label="Visibility radius"
                  helperText="Type in how far away from the Location a user is able to see the location in meters."
                />

                {isEventDateVisible && (
                  <TextInput
                    name="eventDate"
                    type="date"
                    label={t("venue_upsert_form.event_date.label")}
                  />
                )}
                {isShowPersonalCodeVisible && (
                  <CheckboxInput
                    name="showPersonalCode"
                    label={t("venue_upsert_form.show_personal_code.label")}
                  />
                )}

                {isShowMemberCardVisible && (
                  <CheckboxInput
                    name="showMemberCard"
                    label={t("venue_upsert_form.show_member_card.label")}
                  />
                )}
              </div>
            </div>
            <div css={[checkboxGroupStyle, additionalCheckboxGroupStyle]}>
              <StyledText
                color={colors.textPrimary}
                className="bodySubtextSemibold mb-0"
              >
                Location exposure
              </StyledText>
              <CheckboxInput
                name="exposeLocation"
                label={t("venue_upsert_form.expose_location.label")}
              />
            </div>
            <div css={formRowStyle} className="row">
              <div className="col-12 mb-1 mt-2">
                <Inputs.DropzoneFormField
                  name="images"
                  label="Venue logo"
                  onDrop={(acceptedFiles) => setVenueLogo(acceptedFiles?.[0])}
                  selectedFiles={venueLogo ? [venueLogo] : undefined}
                  onSelectedFileRemove={() => setVenueLogo(undefined)}
                  accept=".jpg,.jpeg,.png"
                  maxFiles={1}
                >
                  {venue.logoUrl && !venueLogo && (
                    <div className="row">
                      <VenueUpsertFormImage
                        organization={organization}
                        venueId={venue.id}
                        image={{ url: venue.logoUrl }}
                        showDeleteButton={false}
                      />
                    </div>
                  )}
                </Inputs.DropzoneFormField>
              </div>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <ButtonAction
              onClick={handleSubmit(onSubmit as any)}
              type="button"
              disabled={props.isLoading}
            >
              {props.isLoading ? (
                <>{t("venue_upsert_form.submit_button.label_is_saving")}</>
              ) : (
                <>{t("venue_upsert_form.submit_button.label")}</>
              )}
            </ButtonAction>
          </div>
        </form>
      </FormProvider>
    );
  }
);

export default VenueUpsertForm;
